import { hasOrgRole } from "@archinventory/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { Boxes, Building2, KeyRound, LogOut, Shield, Users } from "lucide-react";
import { LegalLinks } from "../components/legal-layout";
import { Button } from "../components/ui/button";
import { Select } from "../components/ui/input";
import { api, unwrap } from "../lib/api";
import { authClient } from "../lib/auth";
import { LiveProvider, useLiveStatus } from "../lib/live";
import { meQuery, requireMe } from "../lib/session";
import { cn } from "../lib/utils";

export const Route = createFileRoute("/_app")({
  beforeLoad: async ({ context, location }) => ({
    me: await requireMe(context.queryClient, location.href),
  }),
  component: AppShell,
});

function LiveDot() {
  const s = useLiveStatus();
  return (
    <span
      className="flex items-center gap-1.5 text-xs text-muted-foreground"
      title={`Live updates: ${s}`}
    >
      <span
        className={cn(
          "size-2 rounded-full",
          s === "open" ? "bg-success" : s === "connecting" ? "bg-warning" : "bg-destructive",
        )}
      />
      {s === "open" ? "Live" : s === "connecting" ? "Connecting…" : "Offline"}
    </span>
  );
}

function AppShell() {
  const { data: me } = useQuery(meQuery);
  const qc = useQueryClient();
  const navigate = useNavigate();
  if (!me) return null;
  const role = me.activeOrg?.role;
  const nav = [
    { to: "/projects", label: "Projects", icon: Boxes, show: !!me.activeOrg },
    { to: "/clients", label: "Clients", icon: Building2, show: !!me.activeOrg },
    { to: "/members", label: "Members", icon: Users, show: !!role && hasOrgRole(role, "admin") },
    { to: "/admin", label: "Admin", icon: Shield, show: me.user.platformRole === "admin" },
  ] as const;

  return (
    <LiveProvider orgId={me.activeOrg?.id ?? null} userId={me.user.id}>
      <div className="flex h-full flex-col">
        {me.impersonatedBy && (
          <div className="flex items-center justify-center gap-3 bg-warning/30 px-4 py-1.5 text-sm">
            Impersonating {me.user.email}
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                await authClient.admin.stopImpersonating();
                qc.clear();
                window.location.href = "/admin/users";
              }}
            >
              Stop
            </Button>
          </div>
        )}
        <div className="flex min-h-0 flex-1">
          <aside className="flex w-56 shrink-0 flex-col gap-1 border-r border-border bg-muted/30 p-3">
            <p className="px-2 py-2 font-semibold tracking-tight">archinventory</p>
            {me.orgs.length > 1 ? (
              <Select
                aria-label="Organization"
                value={me.activeOrg?.id ?? ""}
                onChange={async (e) => {
                  await unwrap(api.me["active-org"].$post({ json: { orgId: e.target.value } }));
                  qc.clear();
                  await navigate({ to: "/projects" });
                }}
                className="mb-2"
              >
                {me.orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            ) : (
              me.orgs[0] && (
                <p className="mb-2 truncate px-2 text-sm text-muted-foreground">
                  {me.orgs[0].name}
                </p>
              )
            )}
            {nav
              .filter((n) => n.show)
              .map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                  activeProps={{ className: "bg-muted font-medium" }}
                >
                  <n.icon className="size-4" />
                  {n.label}
                </Link>
              ))}
            <div className="mt-auto flex flex-col gap-2 border-t border-border pt-3">
              <LiveDot />
              <Link
                to="/account"
                className="flex items-center gap-1.5 truncate rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                activeProps={{ className: "bg-muted" }}
                title={`${me.user.email} · Account`}
              >
                <KeyRound className="size-3 shrink-0" />
                <span className="truncate">
                  {me.user.name}
                  {role ? ` · ${role}` : ""}
                </span>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                className="justify-start"
                onClick={async () => {
                  await authClient.signOut();
                  qc.clear();
                  await navigate({ to: "/login", search: {} });
                }}
              >
                <LogOut /> Sign out
              </Button>
              <LegalLinks className="flex flex-wrap gap-x-3 gap-y-1 px-2 text-[11px] text-muted-foreground" />
            </div>
          </aside>
          <main className="min-w-0 flex-1 overflow-auto">
            {me.activeOrg?.suspended ? (
              <p className="p-8 text-muted-foreground">
                This organization is suspended. Contact support.
              </p>
            ) : (
              <Outlet />
            )}
          </main>
        </div>
      </div>
    </LiveProvider>
  );
}
